import React, { useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { pluralForm } from "../../i18n/quotaCopy";
import { Badge } from "../ui/badge";

function reportGroupToggle(expanded) {
  // Required on click so the public share page never imports product analytics.
  try {
    const path = typeof window === "undefined" ? "" : window.location.pathname || "";
    if (path === "/share" || path.startsWith("/share/")) return;
    const { track } = require("../../lib/analytics");
    track("history_case_group_toggled", { expanded });
  } catch {
    /* analytics must never break the timeline */
  }
}

const CASE_LINK_STATUSES = new Set(["linked", "pending", "deleted"]);

function isCaseAttachment(item) {
  return Boolean(item && item.source === "case_attachment" && item.query_id);
}

function caseQueryIdsOnPage(records) {
  const ids = new Set();
  for (const item of records) {
    if (item?.kind === "case" && item.query_id) ids.add(item.query_id);
  }
  return ids;
}

function nestsUnderCase(item, caseIds) {
  return isCaseAttachment(item) && item.case?.status === "linked" && caseIds.has(item.query_id);
}

function isStandaloneAttachment(item, caseIds) {
  if (!isCaseAttachment(item) || !CASE_LINK_STATUSES.has(item.case?.status)) return false;
  return !nestsUnderCase(item, caseIds);
}

function byUploadTime(left, right) {
  const a = String(left?.created_at || "");
  const b = String(right?.created_at || "");
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

function groupTimeline(records, { groupLoneAttachments = false } = {}) {
  const caseIds = caseQueryIdsOnPage(records);
  const nested = new Map();
  const standalone = new Map();
  for (const item of records) {
    if (nestsUnderCase(item, caseIds)) {
      const list = nested.get(item.query_id) || [];
      list.push(item);
      nested.set(item.query_id, list);
    } else if (isStandaloneAttachment(item, caseIds)) {
      const list = standalone.get(item.query_id) || [];
      list.push(item);
      standalone.set(item.query_id, list);
    }
  }
  for (const list of nested.values()) list.sort(byUploadTime);
  for (const list of standalone.values()) list.sort(byUploadTime);

  const nestedIds = new Set();
  for (const list of nested.values()) {
    for (const item of list) nestedIds.add(item.record_id);
  }

  const emittedStandalone = new Set();
  const seenCaseQuery = new Set();
  const rows = [];
  for (const item of records) {
    if (nestedIds.has(item.record_id)) continue;
    if (isStandaloneAttachment(item, caseIds)) {
      if (emittedStandalone.has(item.query_id)) continue;
      emittedStandalone.add(item.query_id);
      const files = standalone.get(item.query_id) || [];
      if (files.length === 1 && !groupLoneAttachments) {
        rows.push({ type: "record", item: files[0], caseLink: files[0].case });
      } else {
        rows.push({
          type: "standalone",
          key: item.query_id,
          files,
          anchor: item,
          caseLink: files.find((file) => file.case?.title)?.case || files[0].case,
        });
      }
      continue;
    }
    if (item.kind === "case") {
      const queryId = item.query_id;
      const files = queryId && !seenCaseQuery.has(queryId) ? nested.get(queryId) || [] : [];
      if (queryId) seenCaseQuery.add(queryId);
      rows.push({ type: "case", item, files });
      continue;
    }
    rows.push({ type: "record", item });
  }
  return rows;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileMeasure(item, t) {
  const pages = item?.file?.pages;
  if (typeof pages === "number" && pages > 0) {
    return t("history.filePages").replace("{n}", String(pages));
  }
  const bytes = item?.file?.size_bytes;
  if (typeof bytes === "number" && bytes >= 0) return formatFileSize(bytes);
  return "";
}

function caseContextText(caseLink, t) {
  if (caseLink?.status === "linked") {
    const title = String(caseLink.title || "").trim();
    if (title) return t("history.fromCase").replace("{title}", title);
    const date = String(caseLink.event_date || "").trim();
    if (date) return t("history.fromCaseDated").replace("{date}", date);
    return t("history.fromCaseUntitled");
  }
  if (caseLink?.status === "pending") return t("history.fromCasePending");
  if (caseLink?.status === "deleted") return t("history.fromCaseDeleted");
  return "";
}

function sourceLine(item, t, caseLink) {
  const context = caseContextText(caseLink, t);
  const source = context || t(`history.source.${item.source}`);
  return item.facility ? `${source} · ${item.facility}` : source;
}

function attachedCount(item, files) {
  const declared = Number(item?.attachment_count);
  if (Number.isFinite(declared) && declared > 0) return declared;
  return files.length;
}

function pluralText(t, locale, count, key) {
  const template = pluralForm(locale, count, {
    one: t(`${key}.one`),
    few: t(`${key}.few`),
    many: t(`${key}.many`),
  });
  return template.replace("{n}", String(count));
}

function ExpandFilesButton({ expanded, count, controlId, childrenId, onToggle, t, locale }) {
  const Icon = expanded ? ChevronDown : ChevronRight;
  return (
    <button
      type="button"
      className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      aria-expanded={expanded}
      aria-controls={childrenId}
      onClick={onToggle}
      data-testid={controlId}
    >
      {pluralText(t, locale, count, "history.attachedFiles")}
      <Icon className="h-3 w-3" aria-hidden="true" />
    </button>
  );
}

function OpeningSpinner({ label }) {
  return (
    <Loader2
      className="h-3.5 w-3.5 animate-spin shrink-0 text-muted-foreground"
      aria-label={label}
      data-testid="history-record-opening"
    />
  );
}

function RecordButton({ item, onOpen, t, caseLink, className, opening }) {
  return (
    <button
      type="button"
      className={className || "w-full text-left hover:bg-muted/40"}
      onClick={() => onOpen(item)}
      aria-busy={opening || undefined}
      data-testid={`history-record-${item.record_id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">
          {item.event_date || t("history.dateUnknown")}
        </span>
        <Badge variant="secondary" className="text-[10px]">
          {t(`history.category.${item.category}`)}
        </Badge>
      </div>
      <p className="text-sm font-medium mt-1 flex items-center gap-2 min-w-0">
        <span className="truncate">
          {item.title || t(`history.category.${item.category}`)}
        </span>
        {opening ? <OpeningSpinner label={t("common.loading")} /> : null}
      </p>
      <p
        className="text-xs text-muted-foreground truncate"
        data-testid={caseLink ? `history-case-subtitle-${item.record_id}` : undefined}
      >
        {sourceLine(item, t, caseLink)}
      </p>
    </button>
  );
}

function ChildRow({ item, onOpen, t, opening }) {
  const measure = fileMeasure(item, t);
  return (
    <li>
      <button
        type="button"
        className="w-full text-left py-2 pl-4 hover:bg-muted/40"
        onClick={() => onOpen(item)}
        aria-busy={opening || undefined}
        data-testid={`history-record-${item.record_id}`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">
            {item.event_date || t("history.dateUnknown")}
          </span>
          <Badge variant="secondary" className="text-[10px]">
            {t(`history.category.${item.category}`)}
          </Badge>
          {measure ? <span className="text-xs text-muted-foreground">{measure}</span> : null}
        </div>
        <p className="text-sm mt-1 flex items-center gap-2 min-w-0">
          <span className="truncate">
            {item.title || t(`history.category.${item.category}`)}
          </span>
          {opening ? <OpeningSpinner label={t("common.loading")} /> : null}
        </p>
      </button>
    </li>
  );
}

function FileChildren({ files, listId, onOpen, t, openingId }) {
  if (!files.length) return null;
  return (
    <ul className="ml-3 border-l border-border pb-2" data-testid={listId}>
      {files.map((item) => (
        <ChildRow
          key={item.record_id}
          item={item}
          onOpen={onOpen}
          t={t}
          opening={openingId === item.record_id}
        />
      ))}
    </ul>
  );
}

export default function RecordTimeline({
  records,
  onOpen,
  expandCaseGroups = false,
  openingId = null,
}) {
  const { t, language } = useLanguage();
  const rows = groupTimeline(records || [], { groupLoneAttachments: expandCaseGroups });
  const [expanded, setExpanded] = useState(() => {
    if (!expandCaseGroups) return {};
    const initial = {};
    for (const row of rows) {
      if (row.type === "standalone") initial[`group:${row.key}`] = true;
      if (row.type === "case" && attachedCount(row.item, row.files) > 0) {
        initial[`case:${row.item.record_id}`] = true;
      }
    }
    return initial;
  });

  const toggle = (key) => {
    const next = !expanded[key];
    setExpanded((current) => ({ ...current, [key]: next }));
    reportGroupToggle(next);
  };

  if (!records || records.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center" data-testid="history-empty">
        {t("history.noRecords")}
      </p>
    );
  }

  return (
    <ol className="divide-y divide-border" data-testid="history-timeline">
      {rows.map((row) => {
        if (row.type === "case") {
          const { item, files } = row;
          const key = `case:${item.record_id}`;
          const open = Boolean(expanded[key]);
          const count = attachedCount(item, files);
          const childrenId = `history-case-children-${item.record_id}`;
          return (
            <li key={item.record_id}>
              <div className="py-3 px-1">
                <RecordButton
                  item={item}
                  onOpen={onOpen}
                  t={t}
                  className="w-full text-left hover:bg-muted/40"
                  opening={openingId === item.record_id}
                />
                {count > 0 ? (
                  <ExpandFilesButton
                    expanded={open}
                    count={count}
                    controlId={`history-case-files-${item.record_id}`}
                    childrenId={childrenId}
                    onToggle={() => toggle(key)}
                    t={t}
                    locale={language}
                  />
                ) : null}
              </div>
              {open ? (
                <FileChildren
                  files={files}
                  listId={childrenId}
                  onOpen={onOpen}
                  t={t}
                  openingId={openingId}
                />
              ) : null}
            </li>
          );
        }
        if (row.type === "standalone") {
          const key = `group:${row.key}`;
          const open = Boolean(expanded[key]);
          const childrenId = `history-case-children-${row.key}`;
          const caseTitle = String(row.caseLink?.title || "").trim();
          const subtitle = caseContextText(row.caseLink, t);
          return (
            <li key={key}>
              <div className="py-3 px-1">
                <p className="text-xs text-muted-foreground">
                  {row.caseLink?.event_date || row.anchor.event_date || t("history.dateUnknown")}
                </p>
                {caseTitle ? (
                  <p
                    className="text-sm font-medium mt-1 truncate"
                    data-testid={`history-case-title-${row.key}`}
                  >
                    {caseTitle}
                  </p>
                ) : null}
                <ExpandFilesButton
                  expanded={open}
                  count={row.files.length}
                  controlId={`history-case-files-${row.key}`}
                  childrenId={childrenId}
                  onToggle={() => toggle(key)}
                  t={t}
                  locale={language}
                />
                {!caseTitle && subtitle ? (
                  <p
                    className="text-xs text-muted-foreground truncate"
                    data-testid={`history-case-subtitle-${row.key}`}
                  >
                    {subtitle}
                  </p>
                ) : null}
              </div>
              {open ? (
                <FileChildren
                  files={row.files}
                  listId={childrenId}
                  onOpen={onOpen}
                  t={t}
                  openingId={openingId}
                />
              ) : null}
            </li>
          );
        }
        return (
          <li key={row.item.record_id} className="py-3 px-1">
            <RecordButton
              item={row.item}
              onOpen={onOpen}
              t={t}
              caseLink={row.caseLink}
              opening={openingId === row.item.record_id}
            />
          </li>
        );
      })}
    </ol>
  );
}
